import React, { useEffect } from 'react';
import {
    Box,
    Paper,
    ButtonBase,
    Typography,
    IconButton,
    Fade,
    Chip
} from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const PeriodSelector = ({
    viewType,
    setViewType,
    selectedPeriod,
    setSelectedPeriod,
    availableWeeks,
    availableMonths,
    animationDirection,
    setAnimationDirection
}) => {
    const currentWeekIndex = availableWeeks?.findIndex(week => week.id === selectedPeriod) ?? -1;
    const currentMonthIndex = availableMonths?.findIndex(month => month.id === selectedPeriod) ?? -1;

    useEffect(() => {
        if (viewType === 'weekly' && selectedPeriod === 'all' && availableWeeks?.length > 0) {
            setSelectedPeriod(availableWeeks[availableWeeks.length - 1].id);
        } else if (viewType === 'monthly' && selectedPeriod === 'all' && availableMonths?.length > 0) {
            setSelectedPeriod(availableMonths[availableMonths.length - 1].id);
        } else if (viewType === 'all') {
            setSelectedPeriod('all');
        }
    }, [viewType, selectedPeriod, availableWeeks, availableMonths, setSelectedPeriod]);

    const handlePeriodChange = (direction) => {
        setAnimationDirection(direction);

        if (viewType === 'weekly') {
            const newIndex = direction === 'left' ? currentWeekIndex - 1 : currentWeekIndex + 1;
            if (newIndex >= 0 && newIndex < availableWeeks.length) {
                setSelectedPeriod(availableWeeks[newIndex].id);
            }
        } else if (viewType === 'monthly') {
            const newIndex = direction === 'left' ? currentMonthIndex - 1 : currentMonthIndex + 1;
            if (newIndex >= 0 && newIndex < availableMonths.length) {
                setSelectedPeriod(availableMonths[newIndex].id);
            }
        }
    };

    return (
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Selector Principal */}
            <Paper elevation={0} sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap', // Permitir que los botones bajen en mobile
                gap: { xs: 1, sm: 2 }, // Gap más pequeño en mobile
                p: 1,
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderRadius: '20px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.05)',
                width: '100%', // Asegurar que ocupe el ancho disponible
                maxWidth: '800px', // Limitar ancho máximo
                mx: 'auto' // Centrar
            }}>
                <ButtonBase
                    onClick={() => {
                        setViewType('all');
                        setSelectedPeriod('all');
                    }}
                    sx={{
                        borderRadius: '15px',
                        p: 1.5,
                        px: { xs: 2, sm: 3 }, // Padding horizontal responsive
                        flexGrow: { xs: 1, sm: 0 }, // Ocupar espacio en mobile
                        backgroundColor: viewType === 'all' ? 'rgba(183,10,32,0.2)' : 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: viewType === 'all' ? 'rgba(183,10,32,0.3)' : 'rgba(255,255,255,0.05)',
                        }
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: viewType === 'all' ? '#fff' : 'rgba(255,255,255,0.7)',
                        width: '100%'
                    }}>
                        <AllInclusiveIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Completo
                        </Typography>
                    </Box>
                </ButtonBase>

                <ButtonBase
                    onClick={() => setViewType('monthly')}
                    sx={{
                        borderRadius: '15px',
                        p: 1.5,
                        px: { xs: 2, sm: 3 },
                        flexGrow: { xs: 1, sm: 0 },
                        backgroundColor: viewType === 'monthly' ? 'rgba(183,10,32,0.2)' : 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: viewType === 'monthly' ? 'rgba(183,10,32,0.3)' : 'rgba(255,255,255,0.05)',
                        }
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: viewType === 'monthly' ? '#fff' : 'rgba(255,255,255,0.7)',
                        width: '100%'
                    }}>
                        <CalendarMonthIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Mensual
                        </Typography>
                    </Box>
                </ButtonBase>

                <ButtonBase
                    onClick={() => setViewType('weekly')}
                    sx={{
                        borderRadius: '15px',
                        p: 1.5,
                        px: { xs: 2, sm: 3 },
                        flexGrow: { xs: 1, sm: 0 },
                        backgroundColor: viewType === 'weekly' ? 'rgba(183,10,32,0.2)' : 'transparent',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: viewType === 'weekly' ? 'rgba(183,10,32,0.3)' : 'rgba(255,255,255,0.05)',
                        }
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: viewType === 'weekly' ? '#fff' : 'rgba(255,255,255,0.7)',
                        width: '100%'
                    }}>
                        <DateRangeIcon sx={{ fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Semanal
                        </Typography>
                    </Box>
                </ButtonBase>
            </Paper>

            {/* Navegador de Semanas/Meses */}
            {(viewType === 'weekly' || viewType === 'monthly') && (
                <Paper elevation={0} sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 2,
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    borderRadius: '20px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.05)',
                }}>
                    <IconButton
                        onClick={() => handlePeriodChange('left')}
                        disabled={viewType === 'weekly' ? currentWeekIndex <= 0 : currentMonthIndex <= 0}
                        sx={{
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                            '&.Mui-disabled': { opacity: 0.3 }
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <Fade
                        in={true}
                        direction={animationDirection === 'left' ? 'right' : 'left'}
                        timeout={300}
                    >
                        <Box sx={{
                            textAlign: 'center',
                            minWidth: '260px',
                            animation: 'fadeIn 0.3s ease-out',
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                color: '#fff',
                                mb: 0.5,
                                textTransform: 'capitalize'
                            }}>
                                {viewType === 'weekly'
                                    ? (selectedPeriod !== 'all' && `Semana ${currentWeekIndex + 1} de ${availableWeeks.length}`)
                                    : (selectedPeriod !== 'all' && availableMonths[currentMonthIndex]?.label)
                                }
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '0.9rem'
                            }}>
                                {viewType === 'weekly'
                                    ? (selectedPeriod !== 'all' && availableWeeks[currentWeekIndex]?.label)
                                    : (selectedPeriod !== 'all' && availableMonths[currentMonthIndex]?.year)
                                }
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                mt: 1
                            }}>
                                <Chip
                                    label={`${viewType === 'weekly'
                                        ? (availableWeeks[currentWeekIndex]?.songCount || 0)
                                        : (availableMonths[currentMonthIndex]?.songCount || 0)
                                        } canciones`}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(183,10,32,0.2)',
                                        color: '#fff',
                                        '& .MuiChip-label': { px: 2 }
                                    }}
                                />
                            </Box>
                        </Box>
                    </Fade>

                    <IconButton
                        onClick={() => handlePeriodChange('right')}
                        disabled={
                            viewType === 'weekly'
                                ? currentWeekIndex >= availableWeeks.length - 1
                                : currentMonthIndex >= availableMonths.length - 1
                        }
                        sx={{
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                            '&.Mui-disabled': { opacity: 0.3 }
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Paper>
            )}
        </Box>
    );
};

export default PeriodSelector;
